/* global fetch */

import { Organisations } from '@birdi/types';
import { config } from '../config';
import {
  headers,
  APIResponse,
  stleaf,
  fetchAPI,
  fetchRaw,
  SpanTag,
} from '../helpers';
import {
  ProcessingPlatformRequest,
  SimpleProcessingConfig,
  ProcessingPreset,
} from './processing-jobs-types';

export const get = (st: SpanTag): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v1/organisation/`).toString();
  return fetchAPI(stleaf(st, 'aiyoo6'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getOneById = (
  st: SpanTag,
  orgId: string,
): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v1/organisation/${orgId}`).toString();
  return fetchAPI(stleaf(st, 'aiyoo6'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getRoles = (st: SpanTag): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/roles`;
  return fetchAPI(stleaf(st, 'eej4sa'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getCurrentUserOrgCampaigns = (
  st: SpanTag,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/campaigns`;
  return fetchAPI(stleaf(st, 'eo4toh'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const create = (st: SpanTag, params): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation`;
  return fetchAPI(stleaf(st, 'hoh9ah'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const update = (st: SpanTag, params): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation`;
  return fetchAPI(stleaf(st, 'aa6uog'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const getSeatsCount = (st: SpanTag) => {
  const url = `${config.API_URL}/v1/organisation/seats`;
  return fetchAPI(stleaf(st, 'foh9on'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface StorageInfoType {
  totalUsedStorage: number | null;
  planStorageLimit: number | null;
  isOverLimit: boolean;
}
export const getStorageInfo = (
  st: SpanTag,
): Promise<APIResponse<StorageInfoType>> => {
  const url = `${config.API_URL}/v1/organisation/storage`;
  return fetchAPI(stleaf(st, 'wu0oze'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getProcessingGPInfo = (
  st: SpanTag,
): Promise<APIResponse<{ processingGPUsage: number }>> => {
  const url = `${config.API_URL}/v1/organisation/processing-gp`;
  return fetchAPI(stleaf(st, 'kug2oh'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getProcessingPresets = (
  st: SpanTag,
): Promise<APIResponse<ProcessingPreset[]>> => {
  const url = `${config.API_URL}/v1/organisation/processing/presets`;
  return fetchAPI(stleaf(st, 'q33kel'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};
export const updateBranding = (
  st: SpanTag,
  id: string,
  params,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/${id}/branding`;
  return fetchAPI(stleaf(st, 'aek0du'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

interface AddWorkspace {
  workspaceName: string;
}
export const addWorkspace = (
  st: SpanTag,
  params: AddWorkspace,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/new-workspace`;
  return fetchAPI(stleaf(st, 'shai3e'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export interface WorkspaceUserInfo {
  fname: string | null;
  lname: string | null;
  profilePic: string | null;
}

export interface GetWorkspacesResponse {
  items: {
    id: string;
    name: string;
    primaryOrgId: string | null;
    totalMaps: number;
    totalMissions: number;
    totalUsers: number;
    usedStorageGb: number;
    menuIcon?: string | null;
    createdAt?: string | null;
    creator?: WorkspaceUserInfo | null;
    users?: WorkspaceUserInfo[];
  }[];
  isOverLimit?: boolean;
}

export interface RecentlyInvitedUser {
  scopeId: string;
  userId: string;
  fname: string | null;
  lname: string | null;
  email: string;
  profilePic: string | null;
  roleName: string;
  createdAt: string;
  workspaceCount: number;
  invitedBy: { fname: string | null; lname: string | null } | null;
}

export interface GetRecentlyInvitedUsersResponse {
  items: RecentlyInvitedUser[];
}

export const getRecentlyInvitedUsers = (
  st: SpanTag,
): Promise<APIResponse<GetRecentlyInvitedUsersResponse>> => {
  const url = `${config.API_URL}/v1/organisation/recently-invited-users`;
  return fetchAPI(stleaf(st, 'eesh0i'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getWorkspaces = (
  st: SpanTag,
  userScopeId: string = null,
): Promise<APIResponse<GetWorkspacesResponse>> => {
  const url = userScopeId
    ? `${config.API_URL}/v1/organisation/workspaces/user/${userScopeId}`
    : `${config.API_URL}/v1/organisation/workspaces`;
  return fetchAPI(stleaf(st, 'iosh4o'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/** Workspaces the current user belongs to; optional org-tree filter via a scope in that tree. */
export interface MemberWorkspaceSummary {
  /** user_scope id for auth.switchContext */
  scopeId: string;
  id: string;
  name: string;
  /** Null for primary org row; set for sub-workspaces. */
  primaryOrgId: string | null;
  menuIcon?: string | null;
  /** True when this row is the current session scope (same as GET /v1/users/me/scopes). */
  active: boolean;
}

export const getMyWorkspaces = (
  st: SpanTag,
  opts?: { contextScopeId?: string; includeMenuIcon?: boolean },
): Promise<APIResponse<{ items: MemberWorkspaceSummary[] }>> => {
  const url = new URL(`${config.API_URL}/v1/organisation/workspaces/mine`);
  if (opts?.contextScopeId) {
    url.searchParams.set('contextScopeId', opts.contextScopeId);
  }
  if (opts?.includeMenuIcon) {
    url.searchParams.set('includeMenuIcon', 'true');
  }
  return fetchAPI(stleaf(st, 'oosho2'), url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getWorkspacesOptimized = (st: SpanTag): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/workspaces-optimized`;
  return fetchAPI(stleaf(st, 'iosh4o'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getUsersAndMapsCount = (
  st: SpanTag,
  id: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/workspaces/users-maps-count/${id}`;
  return fetchAPI(stleaf(st, 'ag8aik'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const deleteWorkspace = (
  st: SpanTag,
  deletingWorkspaceId: string,
  selectedWorkspaceId: string,
) => {
  const url = `${config.API_URL}/v1/organisation/workspaces/${deletingWorkspaceId}/instead/${selectedWorkspaceId}`;
  return fetchAPI(stleaf(st, 'to8ogh'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const addUserToWorkspace = (
  st: SpanTag,
  userScopeId: string,
  selectedWorkspaceId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/workspaces/user/${userScopeId}/to-workspace/${selectedWorkspaceId}`;
  return fetchAPI(stleaf(st, 'aic2ri'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
  });
};

export const deleteWorkspaceFromUser = (
  st: SpanTag,
  workspaceId: string,
  userId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/workspaces/${workspaceId}/user/${userId}`;
  return fetchAPI(stleaf(st, 'eeph8o'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const getScopeIdForDeletingUser = (
  st: SpanTag,
  workspaceId: string,
  userId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/workspaces/${workspaceId}/user/${userId}`;
  return fetchAPI(stleaf(st, 'quoo4o'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

type ChartConfig = Record<string, any>;

export const getChartConfigs = (
  st: SpanTag,
): Promise<APIResponse<ChartConfig[]>> => {
  const url = `${config.API_URL}/v1/organisation/ground-references-chart-configs`;
  return fetchAPI(stleaf(st, 'wb64fl'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface OrganisationTag {
  id: string;
  organisationId: string;
  name: string;
  createdAt: string;
}

export const getTags = (
  st: SpanTag,
  organisationId: string,
): Promise<APIResponse<OrganisationTag[]>> => {
  const url = `${config.API_URL}/v1/organisation/${organisationId}/tags`;
  return fetchAPI(stleaf(st, 'caem8o'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const createTag = (
  st: SpanTag,
  organisationId: string,
  name: string,
): Promise<APIResponse<OrganisationTag>> => {
  const url = `${config.API_URL}/v1/organisation/${organisationId}/tags`;
  return fetchAPI(stleaf(st, 'ba1ail'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ name }),
  });
};

export const deleteTag = (st: SpanTag, tagId: string): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/tags/${tagId}`;
  return fetchAPI(stleaf(st, 'ba1ail'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const getActivityTrends = (
  st: SpanTag,
  duration: string,
  startDate: Date,
  endDate: Date,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/activity-trends`;
  return fetchAPI(stleaf(st, 'sae6je'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ duration, startDate, endDate }),
  });
};

export const getProcessingUsageTrends = (
  st: SpanTag,
  duration: string,
  startDate: Date,
  endDate: Date,
): Promise<APIResponse<Array<{ timing: number; processingGP: number }>>> => {
  const url = `${config.API_URL}/v1/organisation/processing-usage-trends`;
  return fetchAPI(stleaf(st, 'eipoc3'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      duration,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
  });
};

export const getActiveUsers = (
  st: SpanTag,
  startDate: Date,
  endDate: Date,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/active-users`;
  return fetchAPI(stleaf(st, 'ahgu1u'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ startDate, endDate }),
  });
};

export interface WorkspaceGeneralInfo {
  teamMembers: number;
  guestCollaborators: number;
  activeWorkspaces: number;
  mapsCreated: number;
  filesUploaded: number;
  storageUsed: number;
}

export const getWorkspaceGeneralInfo = (
  st: SpanTag,
): Promise<APIResponse<WorkspaceGeneralInfo>> => {
  const url = `${config.API_URL}/v1/organisation/workspace-general-info`;
  return fetchAPI(stleaf(st, 'lee4ie'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface DashboardWidgetsResponse {
  widgets: string[];
}

export const getDashboardWidgets = (
  st: SpanTag,
): Promise<APIResponse<DashboardWidgetsResponse>> => {
  const url = `${config.API_URL}/v1/organisation/dashboard-widgets`;
  return fetchAPI(stleaf(st, 'lee4ie'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const updateDashboardWidgets = (
  st: SpanTag,
  widgets: string[],
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/dashboard-widgets`;
  return fetchAPI(stleaf(st, 'aek0du'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ widgets }),
  });
};

export interface OrganisationComment {
  id: string;
  mapId: string;
  mapName: string;
  unread: boolean;
  author: OrganiationCommentUser & { email: string };
  mentions: OrganiationCommentUser[];
  createdAt: string;
  text: string;
}

export interface OrganiationCommentUser {
  id: string;
  fname: string;
  lname: string;
  profilePic?: string | null;
}

export interface GetRecentCommentsOptions {
  mentionsOnly?: boolean;
  limit?: number;
  cursor?: Date;
}

export interface GetRecentCommentsResult {
  comments: OrganisationComment[];
  hasMore: boolean;
  nextCursor: string | null;
}

export const getRecentComments = (
  st: SpanTag,
  options?: GetRecentCommentsOptions,
): Promise<APIResponse<GetRecentCommentsResult>> => {
  const url = new URL(`${config.API_URL}/v1/organisation/comments`);
  const searchParams = new URLSearchParams();

  if (options.limit) searchParams.set('limit', options.limit.toString());
  if (options.cursor) searchParams.set('cursor', options.cursor.toJSON());
  if (options.mentionsOnly) searchParams.set('mentionsOnly', 'true');

  url.search = searchParams.toString();

  return fetchAPI(stleaf(st, 'as2eid'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface GetUnreadCommentMentionsResult {
  count: number;
}

export const getUnreadCommentMentions = (
  st: SpanTag,
): Promise<APIResponse<GetUnreadCommentMentionsResult>> => {
  const url = `${config.API_URL}/v1/organisation/comments/unread-mentions`;
  return fetchAPI(stleaf(st, 'phahc8'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const requestRegionChange = (
  st: SpanTag,
  params: { targetRegion: string },
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/organisation/request-region-change`;
  return fetchAPI(stleaf(st, 'aer3gi'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const getLogo = async (st: SpanTag, organisationId: string) => {
  const url = `${config.API_URL}/v1/organisation/${organisationId}/logo`;
  const response = await fetchRaw(stleaf(st, 'thie4k'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  const data = await response.blob();
  return data;
};
